import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Container,
    Alert,
    CircularProgress,
    Button,
    Chip,
} from '@mui/material';
import axios from 'axios';
import { internshipDomain } from '../util/domain';

const EventPass = () => {
    const { registrationHashId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [registration, setRegistration] = useState(null);

    useEffect(() => {
        const fetchRegistrationData = async () => {
            try {
                const response = await axios.get(`${internshipDomain}/api/registration/${registrationHashId}`);
                if (response.data.success) {
                    setRegistration(response.data.registration);
                }
            } catch (error) {
                setError('Failed to fetch event pass details');
            } finally {
                setLoading(false);
            }
        };

        fetchRegistrationData();
    }, [registrationHashId]);

    const handleDownload = () => {
        // In a real application, this would generate and download a PDF
        window.print();
    };

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (!registration) {
        return (
            <Container maxWidth="sm">
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error || 'Event pass not found'}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1, mt: 1 }}>
                <img
                    src="/logo-vector.svg"
                    alt="Vector India"
                    style={{
                        height: '120px',
                        width: 'auto'
                    }}
                />
            </Box>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Event Pass
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ mb: 3 }}>
                    <Box sx={{
                        border: '2px solid #1976d2',
                        borderRadius: 2,
                        p: 3,
                        mb: 3,
                        textAlign: 'center'
                    }}>
                        <Typography variant="h5" gutterBottom>
                            {registration.event_title}
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                            <Chip
                                label={registration.event_type}
                                color={registration.event_type === 'WORKSHOP' ? 'primary' : 'secondary'}
                                size="small"
                            />
                            <Chip
                                label={registration.is_online ? 'Online' : 'Offline'}
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        <Typography variant="body1" gutterBottom>
                            This pass belongs to
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            {registration.name}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            from
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            {registration.college_name}
                        </Typography>

                        <Box sx={{ mt: 3, textAlign: 'left' }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>Registration ID:</strong> {registration.registration_id}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>Email:</strong> {registration.email}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>Mobile:</strong> {registration.mobile}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>Event Date:</strong> {new Date(registration.event_start_date).toLocaleString()} - {new Date(registration.event_end_date).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                <strong>Venue:</strong> {Boolean(registration.is_online) ? 'Online Event' : registration.venue || 'To Be Announced'}
                            </Typography>
                            {Boolean(registration.is_online) && (
                                <>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        <strong>Meeting Link:</strong> {registration.meeting_link}
                                    </Typography>
                                    {registration.meeting_password && (
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            <strong>Meeting Password:</strong> {registration.meeting_password}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>

                    <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                        {registration.is_online
                            ? 'Please keep this pass handy for the online event'
                            : 'Please present this pass at the event venue'}
                    </Typography>

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleDownload}
                        sx={{ mt: 2 }}
                    >
                        Download Pass
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default EventPass; 